import { useEffect, useMemo, useState } from 'react';

import type { Lane, Stage, TaskDto } from '@botguide/shared';
import {
  LANES,
  LANE_LABELS,
  STAGE_LABELS,
  STORY_POINTS,
  canHoldWork,
  createTaskSchema,
  defaultStageForLane,
  stagesForLane,
  updateTaskSchema,
} from '@botguide/shared';

import {
  Button,
  Dialog,
  ErrorNote,
  Field,
  LaneBadge,
  Select,
  Textarea,
} from '@/components';
import type { SelectOption } from '@/components';
import { formatPoints, formatRelative } from '@/lib/format';
import { useCreateTask, useDeleteTask, useUpdateTask, useUsers } from '@/lib/queries';
import { useCan } from '@/lib/session';

import { PlacementSelect } from './PlacementSelect';
import { weekOptions } from './BoardFilters';

/* -------------------------------------------------------------------------- */

/**
 * Every editable field as a string, because that is what a form control holds.
 * `''` is the empty choice throughout - unassigned, unestimated, no week - and
 * it becomes `null` on the way out, never `''`.
 */
interface FormState {
  featureId: string;
  title: string;
  description: string;
  lane: Lane;
  stage: Stage | null;
  storyPoints: string;
  assigneeId: string;
  plannedWeek: string;
  isBlocked: boolean;
  blockedReason: string;
}

function formStateFor(task: TaskDto | null, featureId = ''): FormState {
  return {
    // The fallback only applies while creating: an existing task's placement
    // is its own, and must never be overwritten by where the dialog was opened.
    featureId: task?.featureId ?? featureId,
    title: task?.title ?? '',
    description: task?.description ?? '',
    lane: task?.lane ?? 'backlog',
    stage: task?.stage ?? null,
    storyPoints: task?.storyPoints === null || task === null ? '' : String(task.storyPoints),
    assigneeId: task?.assigneeId ?? '',
    plannedWeek: task?.plannedWeek ?? '',
    isBlocked: task?.isBlocked ?? false,
    blockedReason: task?.blockedReason ?? '',
  };
}

const POINT_OPTIONS: SelectOption[] = STORY_POINTS.map((points) => ({
  value: String(points),
  label: `${formatPoints(points)} points`,
}));

const LANE_OPTIONS: SelectOption<Lane>[] = LANES.map((lane) => ({
  value: lane,
  label: LANE_LABELS[lane],
}));

/* -------------------------------------------------------------------------- */

export interface TaskDialogProps {
  /** The task to show, or null to create one. */
  task: TaskDto | null;
  open: boolean;
  onClose: () => void;
  /**
   * Where a new task should start, when the dialog is opened from somewhere
   * that already knows - the work tree's "Add task" on a feature.
   *
   * `PlacementSelect` walks back up from a feature id, so supplying this
   * arrives with client, application and project already chosen. Ignored when
   * editing: an existing task's placement is its own.
   */
  initialFeatureId?: string;
}

/**
 * One task, read or edited.
 *
 * The controls a person sees are decided by `useCan`, which reads the same
 * policy table the API enforces - a contractor may change a task but not
 * create or delete one, so those two controls are simply absent rather than
 * present and failing. That is cosmetic; the API refuses regardless.
 *
 * Validation goes through the shared Zod schemas before anything is sent, so
 * the lane/stage pairing and the story-point scale are checked here against
 * the same definition the server will check them against.
 */
export function TaskDialog({ task, open, onClose, initialFeatureId = '' }: TaskDialogProps) {
  const creating = task === null;

  const canUpdate = useCan('update', 'task');
  const canAssign = useCan('assign', 'task');
  const canCreate = useCan('create', 'task');
  /*
   * Moving an existing task asks for `move`, not `create`. Creating one still
   * asks for `create`, because a task has to be placed as it is made and you
   * cannot make one without that permission. `docs/FINDINGS.md` F20.
   */
  const canMovePlacement = useCan('move', 'task');
  const canDelete = useCan('delete', 'task');
  const editable = creating ? canCreate : canUpdate;

  const users = useUsers();

  const update = useUpdateTask();
  const create = useCreateTask();
  const remove = useDeleteTask();

  const [form, setForm] = useState<FormState>(() => formStateFor(task, initialFeatureId));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Reopening on a different card must not show the previous card's edits.
  useEffect(() => {
    if (!open) return;
    setForm(formStateFor(task, initialFeatureId));
    setFieldErrors({});
    setSubmitError(null);
    setConfirmingDelete(false);
  }, [open, task, initialFeatureId]);

  const assigneeOptions = useMemo<SelectOption[]>(
    () =>
      // `canHoldWork` is the one authority on who may hold a task. Filtering
      // on `role !== 'client'` here would also offer `owner`, a value
      // `assertAssignee` rejects server-side - a control offering a value it
      // refuses.
      (users.data ?? [])
        .filter((user) => canHoldWork(user.role))
        .map((user) => ({ value: user.id, label: user.name })),
    [users.data],
  );

  const stageOptions = useMemo<SelectOption<Stage>[]>(
    () => stagesForLane(form.lane).map((stage) => ({ value: stage, label: STAGE_LABELS[stage] })),
    [form.lane],
  );

  const busy = update.isPending || create.isPending || remove.isPending;

  function patchForm(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function changeLane(lane: Lane) {
    // The shared helper decides the landing stage so the form can never build
    // a lane/stage pair the database would reject.
    patchForm({ lane, stage: defaultStageForLane(lane) });
  }

  function submit() {
    setSubmitError(null);
    setFieldErrors({});

    const shared = {
      title: form.title,
      description: form.description,
      lane: form.lane,
      stage: form.stage,
      storyPoints: form.storyPoints === '' ? null : Number(form.storyPoints),
      assigneeId: form.assigneeId === '' ? null : form.assigneeId,
      plannedWeek: form.plannedWeek === '' ? null : form.plannedWeek,
      isBlocked: form.isBlocked,
      blockedReason: form.isBlocked ? form.blockedReason : null,
    };

    if (task === null) {
      const parsed = createTaskSchema.safeParse({ ...shared, featureId: form.featureId });
      if (!parsed.success) return void setFieldErrors(collectIssues(parsed.error.issues));
      create.mutate(parsed.data, {
        onSuccess: onClose,
        onError: (error) => setSubmitError(error),
      });
      return;
    }

    const parsed = updateTaskSchema.safeParse({ ...shared, featureId: form.featureId });
    if (!parsed.success) return void setFieldErrors(collectIssues(parsed.error.issues));
    update.mutate(
      { id: task.id, patch: parsed.data },
      { onSuccess: onClose, onError: (error) => setSubmitError(error) },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={creating ? 'New task' : (task?.title ?? 'Task')}
      description={
        creating ? (
          'Sized and planned now; it can be moved on the board afterwards.'
        ) : task ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            <LaneBadge lane={task.lane} size="sm" />
            <span>Updated {formatRelative(task.updatedAt)}</span>
          </span>
        ) : undefined
      }
      footer={
        <>
          {!creating && canDelete && task ? (
            <Button
              variant="danger"
              size="sm"
              className="mr-auto"
              loading={remove.isPending}
              onClick={() => {
                if (!confirmingDelete) {
                  setConfirmingDelete(true);
                  return;
                }
                remove.mutate(task.id, {
                  onSuccess: onClose,
                  onError: (error) => setSubmitError(error),
                });
              }}
            >
              {confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {editable ? 'Cancel' : 'Close'}
          </Button>
          {editable ? (
            <Button variant="primary" loading={busy} onClick={submit}>
              {creating ? 'Create task' : 'Save changes'}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {submitError ? <ErrorNote error={submitError} /> : null}

        {!editable ? (
          <p className="rounded-md border border-line bg-sunken px-3 py-2 text-[0.8125rem] text-muted">
            You can read this task but not change it.
          </p>
        ) : null}

        <PlacementSelect
          value={form.featureId}
          onChange={(featureId) => patchForm({ featureId })}
          canMove={editable && (creating ? canCreate : canMovePlacement)}
          error={fieldErrors.featureId ?? null}
          hint={
            creating || canMovePlacement
              ? undefined
              : 'Where this task sits is set by the owner. You can change everything else about it.'
          }
        />

        <Field
          label="Title"
          required
          value={form.title}
          error={fieldErrors.title ?? null}
          disabled={!editable}
          onChange={(event) => patchForm({ title: event.target.value })}
        />

        <Textarea
          label="Description"
          rows={4}
          maxLength={5000}
          value={form.description}
          error={fieldErrors.description ?? null}
          disabled={!editable}
          onChange={(event) => patchForm({ description: event.target.value })}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select<Lane>
            label="Lane"
            options={LANE_OPTIONS}
            value={form.lane}
            error={fieldErrors.lane ?? null}
            disabled={!editable}
            onChange={(event) => changeLane(event.target.value as Lane)}
          />

          <Select<Stage>
            label="Stage"
            options={stageOptions}
            value={form.stage ?? ''}
            hint={stageOptions.length === 0 ? 'This lane carries no stage.' : undefined}
            error={fieldErrors.stage ?? null}
            disabled={!editable || stageOptions.length === 0}
            onChange={(event) => patchForm({ stage: (event.target.value || null) as Stage | null })}
          />

          <Select
            label="Story points"
            placeholder="Not estimated"
            options={POINT_OPTIONS}
            value={form.storyPoints}
            hint="One point is one day; five is a week."
            error={fieldErrors.storyPoints ?? null}
            disabled={!editable}
            onChange={(event) => patchForm({ storyPoints: event.target.value })}
          />

          <Select
            label="Assignee"
            placeholder="Unassigned"
            options={assigneeOptions}
            value={form.assigneeId}
            error={fieldErrors.assigneeId ?? null}
            disabled={!editable || !canAssign}
            onChange={(event) => patchForm({ assigneeId: event.target.value })}
          />

          <Select
            label="Planned week"
            placeholder="No week"
            options={weekOptions(form.plannedWeek || null)}
            value={form.plannedWeek}
            hint="Weeks start on a Monday."
            error={fieldErrors.plannedWeek ?? null}
            disabled={!editable}
            onChange={(event) => patchForm({ plannedWeek: event.target.value })}
          />
        </div>

        <div className="rounded-md border border-line bg-sunken/60 p-3">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={form.isBlocked}
              disabled={!editable}
              onChange={(event) => patchForm({ isBlocked: event.target.checked })}
              className="size-4 rounded-xs accent-[var(--danger)]"
            />
            Blocked
          </label>
          <p className="mt-1 text-[0.8125rem] text-muted">
            Blocked is a flag, not a lane - work can be blocked part-way through Build.
          </p>

          {form.isBlocked ? (
            <Textarea
              label="What is blocking it"
              wrapperClassName="mt-3"
              rows={2}
              maxLength={5000}
              value={form.blockedReason}
              error={fieldErrors.blockedReason ?? null}
              disabled={!editable}
              onChange={(event) => patchForm({ blockedReason: event.target.value })}
            />
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */

/** Zod issues keyed by the field they belong to, first one per field wins. */
function collectIssues(
  issues: readonly { path: (string | number)[]; message: string }[],
): Partial<Record<keyof FormState, string>> {
  const errors: Partial<Record<keyof FormState, string>> = {};
  for (const issue of issues) {
    const key = issue.path[0];
    if (typeof key !== 'string') continue;
    const field = key as keyof FormState;
    if (errors[field] === undefined) errors[field] = issue.message;
  }
  return errors;
}
